import { useEffect, useMemo } from 'react';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { faBellSlash } from '@fortawesome/pro-regular-svg-icons';
import { faHouse } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContacts } from 'api/contacts/contacts';
import { updateEngageMessageById } from 'api/engage';
import Content from 'assets/js/components/Content';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import * as DropdownMenu from 'components/dropdown-menu';
import { FilterBarContainer } from 'components/filter/filters-next/FilterBarContainer';
import { FilterGroupBar } from 'components/filter/filters-next/FilterGroupBar';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { SegmentsIndexDrawer } from 'components/filter/filters-next/SegmentIndexDrawer';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { useInitFilters } from 'components/filter/filters-next/useInitFilters';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { EmptyData } from 'components/util';
import { ContactType } from 'constants/contacts';
import { getContactName } from 'customHooks/contactNameDisplay';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { isEmpty } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<Contact>();

const LetterFilters = () => {
    const { error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { filters, didInit } = useFiltersByKey('engage');
    const { pagination, setPagination, sorting, setSorting } = Table.useReactTableState({
        sortingState: [{ id: 'first_name', desc: false }],
    });

    const [isPending, params] = useIsPending(
        {
            type: ContactType.Individual,
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            filters,
            messageIdNoSavedFilters: letter.id,
            include: 'householdWhereHead',
            ...(sorting.length > 0
                ? { [`sortBy${sorting[0].desc ? 'Desc' : ''}`]: sorting[0].id }
                : {}),
        },
        500,
    );

    const { mutateAsync: updateRecipientFilters } = useMutation({
        mutationFn: () =>
            updateEngageMessageById(accountId, letter.id, {
                channel: 'letter',
                recipient_filters: filters,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessageById(accountId, letter.id),
                exact: true,
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unable to sync your filters');
        },
    });

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactsWithFilters(accountId, params),
        queryFn: () => fetchContacts(accountId, params),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('first_name', {
                header: 'Name',
                cell: info => {
                    const contact = info.row.original;

                    return (
                        <p className={'font-medium text-black'}>
                            {contact?.first_name} {contact?.last_name}
                        </p>
                    );
                },
            }),
            columnHelper.accessor('primary_address_formatted', {
                header: 'Primary address',
                enableSorting: false,
                cell: info => info.getValue() ?? <EmptyData />,
            }),
            columnHelper.accessor('head_household', {
                header: 'Envelope name',
                enableSorting: false,
                cell: info => {
                    const contact = info.row.original;
                    const envelopeName =
                        contact.head_household?.envelope_name || getContactName(contact);
                    const icon = contact.head_household ? <FontAwesomeIcon icon={faHouse} /> : null;

                    return (
                        <div className="flex items-center gap-2">
                            {icon}
                            {envelopeName}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('is_address_subscribed', {
                header: () => null,
                enableSorting: false,
                cell: info => {
                    const isSubscribed = info.getValue();

                    if (isSubscribed) {
                        return null;
                    }

                    return (
                        <Badge color="error">
                            <FontAwesomeIcon size={'sm'} icon={faBellSlash} />
                            <p className={'text-xs leading-none'}>Not subscribed</p>
                        </Badge>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const contact = info.row.original;
                    const isHousehold = info.row.original.head_household !== null;
                    if (!isHousehold) {
                        return null;
                    }

                    return (
                        <DropdownMenu.Root key={contact.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <DropdownMenu.LinkItem
                                    to={`/accounts/${accountId}/engage/letters/draft/${letter.id}/edit/filters/contacts/${contact.id}/household/${contact.household_id}/update`}
                                >
                                    Edit envelope name
                                </DropdownMenu.LinkItem>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
                meta: {
                    ignoreLink: true,
                },
            }),
        ],
        [accountId, letter.id],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, data);

    useEffect(() => {
        if (didInit && data) {
            updateRecipientFilters();
        }
    }, [didInit, data, updateRecipientFilters]);

    return (
        <FilterProvider cacheKey={'engage'} resource={'contacts'}>
            <div className={'flex flex-col gap-4 px-4'}>
                {letter.has_recipients_with_name_too_long && (
                    <AlertNotification.Root type={'warning'}>
                        <AlertNotification.Icon />
                        <AlertNotification.Content>
                            <AlertNotification.Title>
                                USPS requires recipient names to be less than 40 characters in
                                length.
                            </AlertNotification.Title>
                            <AlertNotification.Description>
                                You have at least 10 contacts exceeding this limit. To prevent names
                                from being cut off, please edit contact names.
                            </AlertNotification.Description>
                        </AlertNotification.Content>
                    </AlertNotification.Root>
                )}
                <p className={'text-xl font-medium'}>Filter contacts</p>
                <Table.Root
                    isLoading={isPending}
                    variant={'bordered'}
                    isFiltered={!isEmpty(filters)}
                    table={table}
                    isPersisted={false}
                >
                    <Box className={'flex flex-col'}>
                        <div className={'flex flex-col'}>
                            <div
                                className={'flex justify-end border-b border-b-gray-100 px-4 py-3'}
                            >
                                <SegmentsIndexDrawer />
                                <Table.ToggleFullWidth />
                            </div>
                            <FilterBarContainer>
                                <FilterGroupBar
                                    quickFilters={[
                                        'total_contributions',
                                        'recurring_contributions',
                                        'created_at',
                                        'tags',
                                    ]}
                                />
                            </FilterBarContainer>
                        </div>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'primary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No contacts found.</Table.EmptyTitle>
                        </Table.Empty>
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                            <Table.PaginationPerPageDropdown />
                        </Table.Pagination>
                    </Box>
                </Table.Root>
            </div>
        </FilterProvider>
    );
};

const Wrapper = () => {
    const { data: letter } = useCurrentLetter();
    const { didInit } = useFiltersByKey('engage');
    useInitFilters('engage', transformSavedFilterGroups(letter.recipient_filters ?? []));

    if (didInit) {
        return (
            <Content className={'overflow-y-auto'} dynamicWidthClass={'max-w-screen-lg'}>
                <LetterFilters />
            </Content>
        );
    }

    return null;
};

export { Wrapper as LetterFilters };
